import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createApiClient } from '../api/client';
import { useAccessToken } from '../auth/useAuth';
import { actingFirmId, actingFirmQuery, useActingFirmId } from './advisorActingFirm';
import type { AdvisorFirmChoice, AdvisorFirmsResponse, AdvisorForFirm } from './types';


/** The React Query key for `GET /advisor/firms`. Exported so no consumer has to retype
 *  the string — `WorkspaceProvider`'s reset predicate reads it too. */
export const ADVISOR_FIRMS_QUERY_KEY = ['advisor-firms'] as const;

/**
 * The ONE definition of the {@link ADVISOR_FIRMS_QUERY_KEY} query. Every observer of that
 * key builds its options from here, and the shape this caches — the whole
 * `GET /advisor/firms` response — is the shape every observer sees.
 *
 * ⚠️ **Not a tidiness preference; the alternative was a crash** (steward-app#671). React
 * Query stores exactly one `data` value per query key, produced by whichever observer
 * most recently registered a queryFn for it. Two call sites declaring the same key with
 * queryFns that return DIFFERENT shapes therefore each read what the other last wrote:
 *
 *   - People → Advisors (`src/pages/people/Advisors.tsx`, #632) declared
 *     `['advisor-firms']` with `.then(r => r.items)` — an ARRAY;
 *   - `useAdvisorFirm` below (#636, one day later) declared the same key with the whole
 *     `{ items, advisorFor }` response — an OBJECT. It is mounted on every authenticated
 *     page from `Layout.tsx`, so it always populates the cache first, and the object was
 *     what the settings page found there on navigation.
 *
 * The settings page then ran `(firmsQuery.data ?? []).filter(...)` over that object and
 * took the whole route down with `TypeError: filter is not a function` — `?? []` catches
 * `null`/`undefined`, never a truthy non-array. The reverse direction was equally real:
 * once the settings page's own refetch wrote an ARRAY into the cache, `useAdvisorFirm`
 * read `data.advisorFor` as `undefined` and the `/advisor` nav section vanished for a
 * genuine advisor.
 *
 * One factory makes the two shapes the same shape by construction. A consumer wanting a
 * projection (the settings page wants only `items`) uses React Query's `select`, which is
 * per-observer and never changes what is cached.
 */
export function advisorFirmsQueryOptions(token: string | null) {
  return {
    // ⚠️ The acting firm is part of the KEY, not just the URL (steward-app#983): the answer
    // genuinely differs between "which firm am I" states, and one cache entry serving both
    // would hand a resolved `advisorFor` to a tab that has since cleared its choice. The
    // FIRST element stays `'advisor-firms'` because `WorkspaceProvider`'s reset keep-list
    // matches on `queryKey[0]` — do not reorder these.
    queryKey: [...ADVISOR_FIRMS_QUERY_KEY, actingFirmId()],
    queryFn: ({ queryKey }: { queryKey: readonly unknown[] }) =>
      createApiClient(token!).get<AdvisorFirmsResponse>(`/advisor/firms${actingFirmQuery(queryKey[1])}`),
    enabled: !!token,
    staleTime: 5 * 60_000,
    // No `retry` override here on purpose — this inherits `retry: 1` from `main.tsx`, and
    // should keep it. `retry: false` was considered and REJECTED for steward-app#794: the
    // hold that made a second attempt expensive is time-bounded now
    // ({@link useHoldForAdvisorQuery}), so a retry costs the user no waiting at all, only one
    // background request. It is what eventually gets an advisor on a flaky connection their
    // own portal nav instead of leaving them on the family nav until something else
    // refetches. And this factory is SHARED — People → Advisors spreads it too, a direct
    // navigation with its own loading and error states that would lose its retry for no
    // benefit. (`useFolderVisibility`'s `retry: false`, #796, is not a precedent for this
    // one: a refusal there is a settled answer about what the caller may read, so retrying
    // is pointless by construction. A failed `GET /advisor/firms` is an ordinary transient
    // network failure, which a retry can genuinely fix.)
  };
}

/** `localStorage` key for {@link rememberedFirmWorkspaceId}. `steward-app:` prefix matches
 *  the existing convention (`steward-app:workspaceId` in WorkspaceProvider,
 *  `steward-app:advisorNudgeDismissed` in AdvisorLandingNudge). */
const FIRM_WORKSPACE_HINT_KEY = 'steward-app:advisorFirmWorkspaceId';

/**
 * The firm workspace id this browser last saw a resolved `GET /advisor/firms` name, or
 * `null`. **A PAINT-TIMING HINT, NOTHING ELSE.**
 *
 * `useAdvisorFirm()`'s own header is emphatic that `advisorFor` is UX gating, never
 * authorization — this value is a full step weaker than even that: it is not backed by a
 * live request at all, just whatever this browser last wrote to `localStorage`, which a
 * user (or a browser extension, or a stale tab from a former session) can trivially clear,
 * forge, or leave stale. It exists ONLY so a caller that would otherwise have to wait out
 * `useAdvisorFirm()`'s first request on a cold load can choose to hold paint for that one
 * request instead of committing to a guess. **It must never be upgraded into an optimistic
 * redirect, an optimistic nav reveal, or any other decision that outlives the real query
 * settling** — every one of those stays exactly as gated on `advisorFor` (the actual query
 * result) as it is today. The worst a stale or forged value here can do is cost a legitimate
 * caller one extra render's worth of held paint, which the real query then corrects.
 *
 * Written by {@link useAdvisorFirm} once its query settles (see that effect's own comment);
 * read here by any caller that wants the hint. Consumers: `HomeRoute` (hold `/` rather than
 * flash Dashboard before redirecting to `/advisor`) and `Layout`'s `SidebarContent` (hold the
 * six family pillars rather than paint-then-drop them) — steward-app#700 follow-up.
 */
export function rememberedFirmWorkspaceId(): string | null {
  try {
    return localStorage.getItem(FIRM_WORKSPACE_HINT_KEY);
  } catch {
    return null;
  }
}

/**
 * How long a caller may hold paint waiting for `GET /advisor/firms` to settle. Short on
 * purpose: the hold exists to swallow a sub-second cold-load flash, and anything past
 * that is a blank shell, which is strictly worse than the flash it prevents.
 */
export const ADVISOR_HOLD_MAX_MS = 500;

/**
 * The ONE definition of the cold-load hold shared by `HomeRoute` and `Layout`'s
 * `SidebarContent` — the two callers that would otherwise flash the wrong thing (Dashboard,
 * and the six family pillars) for an advisor in their own firm workspace before
 * `useAdvisorFirm()`'s query settles. See {@link rememberedFirmWorkspaceId}'s header above
 * for what the hint is and, emphatically, what it is not.
 *
 * **Why a shared hook rather than the one-line expression each file used to carry**
 * (steward-app#794): the two copies were written independently (HomeRoute in the #700
 * follow-up, Layout in #703) and had already drifted apart in their comments. A timing rule
 * kept in two places drifts again, and a hold that ends at different moments in the nav and
 * in the page body is exactly the half-painted shell this is meant to avoid.
 *
 * **The hold is BOUNDED, and that bound is the point.** Holding until the query settles
 * meant, on a hanging (not refused) connection, app chrome with no navigation and no page
 * body for ~31s — two 15s attempts plus backoff. That request still takes as long as it
 * takes; what changed is that the SHELL is no longer held for it. React Query's own retry
 * policy is not a paint-timing policy, and this hook must never be re-derived from it (nor
 * the retry trimmed to shorten a hold — see `advisorFirmsQueryOptions` above). After
 * {@link ADVISOR_HOLD_MAX_MS} this returns false even though the query is still loading, and
 * the callers fall through to their pre-#700 behaviour: the family nav and the Dashboard.
 * That is a correct, navigable degradation, not an error state — the query is still in
 * flight, and whatever it answers still lands a moment later, redirecting or re-scoping the
 * nav then. Do NOT "fix" that late correction by extending the bound.
 *
 * Offline is a non-case: React Query pauses rather than fetches, so `isLoading` is false and
 * the hold never engages at all.
 *
 * @param advisorFirmLoading `isLoading` from {@link useAdvisorFirm} in the CALLING component
 *   — passed in rather than read here so this hook adds no second query observer, and so the
 *   caller's own mock of that hook (every suite mocks it) still governs its own test.
 * @param activeWorkspaceId the active workspace id. `!!activeWorkspaceId` guards a
 *   coincidence, not a real match: BOTH sides read `null` for any brand-new session, advisor
 *   and family alike, and holding paint for that shared null would spend exactly the cost
 *   this hint exists to avoid on every family's very first-ever load.
 * @param alsoHoldWhen a SECOND reason to hold, OR-ed with the remembered-id hint above and
 *   subject to the same bound. Defaults to false, so `Layout`'s call is unchanged.
 *
 *   `HomeRoute` passes `workspaceKind === 'firm'` (steward-app#1212, raised by this PR's code
 *   review). The `localStorage` hint is absent on an advisor's very first cold load in the
 *   firm workspace, in a cleared or blocked storage, and in any fresh browser — and what that
 *   session now falls through to is a page saying their portal is not ready, which is a claim
 *   about their account rather than merely the wrong page. The mounted App Pack is a
 *   SERVER-supplied fact about the active membership (`useWorkspaceKind`), so it is a
 *   strictly better reason to wait one beat than the hint is; it is still only ever a reason
 *   to WAIT, never to decide, and everything {@link rememberedFirmWorkspaceId}'s header says
 *   about that applies here unchanged.
 */
export function useHoldForAdvisorQuery(
  advisorFirmLoading: boolean,
  activeWorkspaceId: string | null | undefined,
  alsoHoldWhen = false,
): boolean {
  const wantsHold = advisorFirmLoading
    && (alsoHoldWhen || (!!activeWorkspaceId && activeWorkspaceId === rememberedFirmWorkspaceId()));
  return useBoundedHold(wantsHold, ADVISOR_HOLD_MAX_MS);
}

/**
 * The bounded-hold PRIMITIVE behind {@link useHoldForAdvisorQuery} above, factored out for
 * `Layout.tsx`'s OTHER cold-load flash (steward-app#1402): the per-workspace credentials
 * probe (`useCredentialsReadProbe`, `src/lib/useCanReadCredentials.ts`) needs the identical
 * "hold until X or `maxMs`, whichever comes first" shape, on its own condition and its own
 * settlement signal, not `useHoldForAdvisorQuery`'s advisor-query-specific one. Extracting
 * this is what keeps there being exactly ONE bounded timer in the codebase rather than two
 * copies that read alike today and drift apart the next time either is touched — see this
 * file's own header above (steward-app#794) for why a duplicated timing rule is the thing
 * to avoid.
 *
 * `true` for as long as `wantsHold` stays true, up to `maxMs` of wall-clock time PER
 * EPISODE KEY, then `false` for that key regardless of whether whatever `wantsHold` was
 * waiting on has actually answered. "Per episode key", precisely: the clock for a given
 * key starts the first render `wantsHold` is true under that key and runs for `maxMs`
 * UNINTERRUPTED — it is not "per page load" or "per mount", so a caller whose key changes
 * mid-flight (a cold load's `null` key resolving to a real id, `Layout.tsx`'s credentials
 * hold does exactly this — see its own comment) starts a SECOND `maxMs` window under the
 * new key rather than inheriting whatever was left of the first, and a caller whose key
 * stays fixed while wantsHold's own reason changes underneath it (also `Layout.tsx` — the
 * cold-load term and the ordinary `inClientWorkspace` term can hand off to each other
 * without the key ever changing) sees no restart at all, because nothing in this hook's
 * inputs changed.
 *
 * ⚠️ **`episodeKey` is what makes "a fresh window per episode" true when `wantsHold` can
 * stay continuously `true` ACROSS two different things being waited on (steward-app#1402
 * code review, two rounds).** The naive reset-on-`false` shape is enough when the only way
 * back into a pending state is a `false` in between (a token change, a refetch, a
 * `staleTime` expiry all produce one) — that is still exactly what `useHoldForAdvisorQuery`
 * above relies on, and it passes no `episodeKey`, so it is unaffected by any of this. It is
 * NOT enough for `Layout.tsx`'s credentials hold: switching from client workspace A to
 * client workspace B can leave `wantsHold` (`inClientWorkspace && !settled`) `true` on both
 * sides of the switch — a fresh cold query key for B, but never a `false` in between to
 * reset a bare `boundElapsed` boolean — so a stale `true` left over from A's expired window
 * would apply to B on its very first render, before this hook's own effect has had a
 * chance to run. (An effect that added `activeWorkspaceId` to its deps would still be one
 * render too late: the reset it schedules lands AFTER the render that already painted B as
 * unheld.) `episodeKey` fixes THAT by recording WHICH episode a completed bound belongs to
 * (`elapsedFor`) instead of a bare boolean, so `elapsedFor`'s key is compared against the
 * CURRENT episode on every render, not just updated after one: a bound reached during A's
 * episode (`elapsedFor.key === 'A'`) never reads as elapsed for B's (`episodeKey === 'B'`),
 * because those keys differ NOW, on B's first render, with no effect needing to run first.
 *
 * ⚠️ **A -> B -> back to A is a SECOND bug the first round of `episodeKey` did not close**
 * (steward-app#1402 code review, round 2). If A's own bound had already elapsed
 * (`elapsedFor = { key: 'A' }`) before switching to B, that record survives in state,
 * untouched, for as long as B's own episode is running — nothing before this comment ever
 * clears it, because the code path that WOULD (`!wantsHold`) never runs while `wantsHold`
 * stays continuously `true` across both switches. Switch back to A a moment later and
 * `episodeKey` is `'A'` again: `Object.is(elapsedFor.key, episodeKey)` reads `true` again
 * too, so A's BRAND NEW visit reads as already-elapsed on its first render, holding not at
 * all instead of for a fresh `maxMs`. The effect below now drops an `elapsedFor` that
 * belongs to a DIFFERENT key than the one about to start a new timer, the first moment that
 * mismatch is visible (this effect running for the new episode) rather than waiting for
 * that stale record's own key to recur. A record that matches the CURRENT key is left
 * alone — that is the ordinary case of the same episode's own bound completing.
 *
 * The default `episodeKey = null` makes every call site that never passes one behave
 * exactly as before either fix: `null === null` is always true, so a caller with no episode
 * key is always "the same episode" as itself, which is the single-episode-at-a-time shape
 * this hook had before steward-app#1402 touched it.
 */
export function useBoundedHold(wantsHold: boolean, maxMs: number, episodeKey: unknown = null): boolean {
  const [elapsedFor, setElapsedFor] = useState<{ key: unknown } | null>(null);

  useEffect(() => {
    // Reset rather than latch: the condition can legitimately return to a pending state
    // more than once per mount, and each such episode deserves its own fresh window, not
    // one already spent by an earlier episode. This clears `elapsedFor` unconditionally
    // whenever `wantsHold` drops, regardless of which episode it belonged to — there is
    // nothing left to compare it against once nothing is being held.
    if (!wantsHold) {
      setElapsedFor(null);
      return;
    }
    // Drop an elapsed record left over from a DIFFERENT episode before starting this
    // episode's own timer (steward-app#1402 code review, round 2 — see this hook's own
    // header for the A -> B -> A bug this closes). Without this, a record stays in state
    // untouched until ITS OWN key recurs, which for a small, reused set of keys (workspace
    // ids revisited on a switch back) can be much later than the episode it belonged to.
    // A record matching the CURRENT key is left alone: that is this same episode's own
    // bound, not a stale one, and clearing it here would restart a window that is already
    // correctly running.
    setElapsedFor((prev) => (prev && !Object.is(prev.key, episodeKey) ? null : prev));
    const timer = setTimeout(() => setElapsedFor({ key: episodeKey }), maxMs);
    return () => clearTimeout(timer);
  }, [wantsHold, maxMs, episodeKey]);

  // `Object.is`, not `===`: `episodeKey` may legitimately be `NaN` for a caller with no
  // better key (it never is today, but `===` silently treating two NaNs as different
  // episodes would be exactly the kind of one-off bug this comparison exists to avoid).
  return wantsHold && !(elapsedFor !== null && Object.is(elapsedFor.key, episodeKey));
}

/** Write (or clear) the hint {@link rememberedFirmWorkspaceId} reads. Private to this
 *  module — every write goes through {@link useAdvisorFirm}'s own settle effect below, so
 *  there is exactly one place that decides what this browser remembers. */
function persistFirmWorkspaceHint(firmWorkspaceId: string | null): void {
  try {
    if (firmWorkspaceId) localStorage.setItem(FIRM_WORKSPACE_HINT_KEY, firmWorkspaceId);
    else localStorage.removeItem(FIRM_WORKSPACE_HINT_KEY);
  } catch {
    // ignore storage failures (e.g. private browsing) — same as AdvisorLandingNudge
  }
}

/**
 * Backed by `GET /advisor/firms` (docs/25 §2, S7). Decides whether the SPA SHOWS the
 * `/advisor` nav section, routes, and the post-login landing nudge — nothing more.
 *
 * ⚠️ **UX GATING ONLY, NEVER AUTHORIZATION.** Read the façade handler's own header
 * (`services/steward-app-api/src/handlers/advisor.ts`, `listFirms`) before touching
 * this. `advisorFor` truthy does not grant this session anything, and falsy does not
 * withhold anything: every read the advisor portal makes is a request against a
 * specific workspace, and appcore's membership check on THAT request is the entire
 * boundary, independent of what this hook says. A route guard built from this value
 * may hide a page from someone who isn't an advisor; it must never be treated as the
 * reason a page's data is safe to show one who is. If this call fails, is slow, or
 * simply returns `advisorFor: null` for a real advisor (e.g. a session started before
 * `/me` on the firm workspace propagated), the correct failure is an absent nav item
 * and an unreachable-by-click page — never a data leak, because there is no data path
 * that reads this value instead of asking appcore again.
 *
 * `enabled: !!token` rather than gating on a workspace: this is workspace-LESS by
 * design (the façade reads the caller's own `/me` memberships, not the active
 * workspace), so it resolves before `WorkspaceProvider`'s own `/me` fetch would even
 * matter and works identically regardless of which workspace happens to be active.
 */
export function useAdvisorFirm() {
  const token = useAccessToken();
  // Subscribed rather than merely read, so choosing a firm re-keys the query above and this
  // hook's consumers re-render on the new answer instead of the cached ambiguous one.
  useActingFirmId();
  const { data, isLoading, isError, refetch } = useQuery(advisorFirmsQueryOptions(token));
  const advisorFor = (data?.advisorFor ?? null) as AdvisorForFirm | null;
  // ⚠️ `Array.isArray` rather than truthiness, for the reason every other optional field on
  // these payloads is read defensively: the API and the bundle deploy separately
  // (steward-app#319), so a façade that predates steward-app#983 sends nothing here and a
  // future one must not be able to turn a non-array into a rendered chooser.
  const raw = (data as { firmChoice?: unknown } | undefined)?.firmChoice;
  const firmChoice = (Array.isArray(raw) && raw.length > 0 ? raw : null) as AdvisorFirmChoice | null;

  // Persist the paint-timing hint (see rememberedFirmWorkspaceId's header) once this
  // query settles: write the firm workspace id on a real advisor, or REMOVE the stored
  // id once a query settles WITHOUT one — a revoked or former advisor must not keep
  // holding paint on account of a hint nobody will ever confirm again.
  //
  // This hook is called from several components (Layout, HomeRoute, WorkspaceSwitcher,
  // RequireAdvisorFirm, AdvisorLandingNudge), so this effect runs once per mounted
  // caller rather than once per session. That is harmless, not a bug to fix later: the
  // write is idempotent (same key, same value — or the same no-op remove) no matter how
  // many callers' effects fire it.
  useEffect(() => {
    if (isLoading) return;
    persistFirmWorkspaceHint(advisorFor?.firmWorkspaceId ?? null);
  }, [isLoading, advisorFor?.firmWorkspaceId]);

  return {
    /** True until the first response (success or failure) settles. */
    isLoading,
    /**
     * True once the query has settled WITHOUT a usable response (steward-app#1742).
     * Read (as `isLoading || isError`, "the lookup is unsettled") by `Settings.tsx` and
     * `RedirectFirmAdminToFirmPage.tsx` — both pass it into `isKnownFamilyWorkspace`
     * (`workspace/useWorkspaceKind.ts`) so a DEGRADED packs read whose lookup has failed hides
     * Members the same way one that is still loading does, rather than a failed lookup reading
     * as "settled, no firm here" and showing Members to someone who may run a firm — and, as of
     * the same review round, by `RequireFirmAdmin.tsx` (`isFirmAdminWorkspace`,
     * `advisor/firmAdmin.ts`), for the mirror-image reason: that same degraded-and-unsettled
     * quadrant must stay REACHABLE on the Firm page, not fall through to a hard refusal, so a
     * caller who may turn out to run a firm is not stranded between a hidden Members tab and a
     * refused Firm page while both queries are still working it out. Every other consumer of
     * this hook still collapses "loading", "errored" and "settled with nothing" into the same
     * falsy `advisorFor`, unchanged.
     */
    isError,
    /**
     * Re-runs `GET /advisor/firms` on demand (steward-app#1742) — the "Try again" control
     * `RequireFirmAdmin` shows on a failed or not-yet-resolved lookup. Same underlying
     * React Query observer as `isLoading`/`isError` above, so a retry here is visible to
     * every other mounted caller of this hook too, exactly as a background refetch from
     * any of them already is (see `advisorFirmsQueryOptions`'s own `retry` note).
     */
    refetch,
    /** The caller's own advisor relationship, or null. See the header above. */
    advisorFor,
    /**
     * The firms the FAÇADE says this caller is staff at, present ONLY when it could not
     * tell which one this request is for (steward-app#983).
     *
     * ⚠️ **Non-null here means `advisorFor` is null for a reason that is NOT "not an
     * advisor".** A consumer that reads only `advisorFor` hides the portal from an operator
     * staff at two registered firms — the identity docs/30 T6 creates on purpose — instead
     * of asking them which firm they are working as. `RequireAdvisorFirm` is where that
     * question gets rendered; `Layout` and `HomeRoute` are what get them to it.
     *
     * ⚠️ **Which consumers read it, and which deliberately do not** (raised in this PR's
     * code review, which is right that the rest of the app still collapses three states
     * into two). `RequireAdvisorFirm`, `Layout` and `HomeRoute` read it, because those three
     * decide whether the chooser is REACHABLE at all. `SharedDocuments`, `WorkspaceSwitcher`
     * and `AdvisorLandingNudge` still gate on `advisorFor` alone, so an unresolved caller
     * reads to them as a family member: a family-voiced sentence, no landing nudge. Each of
     * those three is cosmetic, each fails toward LESS, and every one corrects itself the
     * moment the operator answers the question. Threading `firmChoice` through them would be
     * three more files for a state that lasts one click, so it is a named residual rather
     * than an oversight. If any of them ever grows a decision that is not cosmetic, it joins
     * the first three.
     *
     * ⚠️ **`RequireFamilyWorkspace` and `Settings` used to be on that residual list and are
     * NOT any more (steward-app#1307, 2026-09-08).** They were the counterexample the last
     * sentence anticipated: their decision was never cosmetic — it offered a firm's own
     * staff a page whose writes (advisor invitations, a "Shared with «firm»" folder) would
     * land in the firm's own workspace rather than a client's, which is steward-app#798's
     * defect. Both now key on `useWorkspaceKind()` FIRST: a fact about the WORKSPACE,
     * answered from the active membership's mounted packs, so an unresolved `advisorFor` no
     * longer decides it. What stays unrecognised after that is narrower: a firm workspace
     * that never mounted the `steward-advisor-firm` pack, where the remaining
     * `|| (!!advisorFor && ...)` arm does still need `advisorFor` to name it. So do not add
     * these two back to the list above — but do not read them as fully covered either.
     *
     * **`firmChoice` could not close that residual even threaded through**, which is why it
     * belongs to steward-app#736 and not here: the arm needs a workspace id to compare
     * `activeWorkspaceId` against, and `firmChoice` is `AdvisorFirmSummary[]`, which carries
     * `firmId`/`name`/`brandKey` and no `firmWorkspaceId` at all — only `AdvisorForFirm`
     * extends it with that field. Read `types.ts` before assuming otherwise; this is the
     * shape as of 2026-09-08.
     *
     * Update, 2026-09-23: steward-app#736 narrowed that arm rather than deleting it — it is
     * now gated on `!activeMembership` too (the shared `isFirmWorkspace` helper,
     * `workspace/useWorkspaceKind.ts`), so it only ever fires during the membership-loading
     * window this file's own comment above (`activeWorkspaceId` optimistic from
     * `localStorage`, `GET /advisor/firms` resolving before `GET /me`) describes. This
     * paragraph's point stands unchanged: `firmChoice` still cannot name a `firmWorkspaceId`
     * either way.
     */
    firmChoice,
  };
}

/**
 * How often {@link useAdvisorFirmRecheck} re-fetches `GET /advisor/firms` while an advisor
 * sits on {@link FirmWorkspaceNotReady} (steward-app#1271). That page's copy promises "this
 * page will update on its own" — false without something re-checking, since nothing else
 * does and the user would otherwise have to reload.
 *
 * **30 seconds, and both bounds it sits inside belong to the server, not to a client
 * guess.** A poll can genuinely pick up a newly-resolved `advisorFor`, because the façade
 * derives it from `getMe(token)` on every request — there is no cache of its own on that
 * half. But whatever part of this state is directory-driven is cached:
 * `CACHE_TTL_MS = 5 * 60_000` (`services/steward-app-api/src/services/firmDirectory.ts`)
 * and the publisher feeding it runs on a 5-minute cycle (`deploy/lib/firm-directory.ts`) —
 * polling faster than that buys nothing there and only adds load. 30s sits comfortably
 * inside both: responsive against the per-request half, free against the cached half. Do
 * not lower it, and do not lower either of the other two figures in order to justify
 * lowering it — both belong to another owner (out of scope for this file).
 */
export const ADVISOR_RECHECK_INTERVAL_MS = 30_000;

/**
 * The re-check loop {@link FirmWorkspaceNotReady} promises in its own copy, and the
 * `refetch` behind its "Check again" control. A THIRD observer of
 * {@link ADVISOR_FIRMS_QUERY_KEY} (`useAdvisorFirm` above is the other shape), and
 * deliberately NOT folded into that hook or into {@link advisorFirmsQueryOptions} itself.
 *
 * ⚠️ **`refetchInterval` is a per-OBSERVER React Query setting, not a per-cache-entry one —
 * that is the whole reason this is its own hook** (steward-app#1271, raised by this issue's
 * own brief). `Layout`, `HomeRoute`, People → Advisors, `SharedDocuments`,
 * `RequireAdvisorFirm`, `WorkspaceSwitcher` and `AdvisorLandingNudge` all read the SAME
 * cache entry through `useAdvisorFirm`, and none of them gain a poll from this one setting
 * an interval on its own, separate observer (steward-app#671 is the scar from assuming a
 * shared query key means shared behaviour — see `advisorFirmsQueryOptions`'s header). This
 * hook has exactly one caller, `FirmWorkspaceNotReady`, which itself mounts only in the
 * exact state the interval is for: `workspaceKind === 'firm'`, `advisorFor` and
 * `firmChoice` both null (see `HomeRoute.tsx`'s branch order). Mounting IS the condition;
 * there is no separate flag threaded in to re-check.
 *
 * Stopping is equally implicit, not a decision this hook makes: the moment `advisorFor` (or
 * `firmChoice`) resolves, `HomeRoute` re-renders past the `FirmWorkspaceNotReady` branch,
 * the component unmounts, and this observer — and whatever interval timer it was holding —
 * unmounts with it. There is nothing left mounted to keep polling.
 *
 * `refetchIntervalInBackground` is left at React Query's own default (`false`) on purpose —
 * do not pass `true`. A backgrounded tab should not spend a network request every 30s on a
 * page nobody is looking at.
 */
export function useAdvisorFirmRecheck() {
  const token = useAccessToken();
  // Same reason as `useAdvisorFirm` above: subscribed so a change of acting firm re-keys
  // this observer's query too, not just the other one's.
  useActingFirmId();
  const { isFetching, refetch } = useQuery({
    ...advisorFirmsQueryOptions(token),
    refetchInterval: ADVISOR_RECHECK_INTERVAL_MS,
  });
  return {
    /** True while a request against this shared query is in flight — ordinarily this
     *  hook's own interval tick or `recheck()`, but `isFetching` reads the QUERY's shared
     *  fetch state (steward-app#671 again: one cache entry, several observers), so a
     *  background refetch from any of the other observers listed above (e.g. one of them
     *  refetching on window focus after the 5-minute `staleTime` lapses) also flips this
     *  true while mounted here. Harmless — "Check again" briefly reads "Checking…" for a
     *  refetch this component didn't start, never the reverse — but it is not a claim
     *  that ONLY this hook's own triggers can set it. {@link FirmWorkspaceNotReady} uses
     *  it for its "Check again" control's pending state; it must never be read as "the
     *  recheck succeeded". */
    isRechecking: isFetching,
    /** Triggers an immediate re-fetch, independent of the interval's own schedule. */
    recheck: refetch,
  };
}
